type AlertType = "error" | "success";

interface AlertOptions {
  title?: string;
  type?: AlertType;
}

/**
 * 命令式弹窗（可在 axios 拦截器等非 React 上下文中调用）
 * 用户点击「确定」后 Promise resolve
 */
export function showAlert(message: string, options: AlertOptions = {}): Promise<void> {
  const { title = "提示", type = "error" } = options;
  const isSuccess = type === "success";

  return new Promise((resolve) => {
    const existing = document.getElementById("global-alert-dialog");
    if (existing) existing.remove();

    const overlay = document.createElement("div");
    overlay.id = "global-alert-dialog";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      zIndex: "9999",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.4)",
    });

    const panel = document.createElement("div");
    Object.assign(panel.style, {
      background: "#fff",
      borderRadius: "12px",
      width: "100%",
      maxWidth: "28rem",
      margin: "0 1rem",
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
      overflow: "hidden",
    });

    const header = document.createElement("div");
    Object.assign(header.style, {
      padding: "1rem 1.25rem",
      borderBottom: "1px solid #e5e7eb",
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
    });

    const iconWrap = document.createElement("div");
    Object.assign(iconWrap.style, {
      width: "2.25rem",
      height: "2.25rem",
      borderRadius: "9999px",
      background: isSuccess ? "#dcfce7" : "#fee2e2",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: "0",
      color: isSuccess ? "#16a34a" : "#dc2626",
      fontSize: "1.125rem",
    });
    iconWrap.innerHTML = isSuccess
      ? '<i class="ri-checkbox-circle-line"></i>'
      : '<i class="ri-error-warning-line"></i>';

    const titleEl = document.createElement("h3");
    Object.assign(titleEl.style, {
      margin: "0",
      fontSize: "0.875rem",
      fontWeight: "600",
      color: "#111827",
    });
    titleEl.textContent = title;

    header.append(iconWrap, titleEl);

    const body = document.createElement("div");
    Object.assign(body.style, {
      padding: "1rem 1.25rem",
      fontSize: "0.875rem",
      color: "#4b5563",
      lineHeight: "1.625",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    });
    body.textContent = message;

    const footer = document.createElement("div");
    Object.assign(footer.style, {
      padding: "1rem 1.25rem",
      borderTop: "1px solid #e5e7eb",
      display: "flex",
      justifyContent: "flex-end",
    });

    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.textContent = "确定";
    const btnColor = isSuccess ? "#16a34a" : "#ef4444";
    const btnHover = isSuccess ? "#15803d" : "#dc2626";
    Object.assign(okBtn.style, {
      padding: "0.5rem 1rem",
      borderRadius: "0.5rem",
      border: "none",
      background: btnColor,
      color: "#fff",
      fontSize: "0.875rem",
      fontWeight: "500",
      cursor: "pointer",
    });

    const close = () => {
      overlay.remove();
      resolve();
    };

    okBtn.addEventListener("click", close);
    okBtn.addEventListener("mouseenter", () => {
      okBtn.style.background = btnHover;
    });
    okBtn.addEventListener("mouseleave", () => {
      okBtn.style.background = btnColor;
    });

    footer.appendChild(okBtn);
    panel.append(header, body, footer);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    okBtn.focus();
  });
}

export function alertError(message: string, title = "提示"): Promise<void> {
  return showAlert(message, { title, type: "error" });
}

export function alertSuccess(message: string, title = "提示"): Promise<void> {
  return showAlert(message, { title, type: "success" });
}

interface ConfirmOptions {
  title?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

/**
 * 命令式确认框，确认 resolve(true)，取消 resolve(false)
 */
export function showConfirm(message: string, options: ConfirmOptions = {}): Promise<boolean> {
  const {
    title = "提示",
    confirmLabel = "确定",
    cancelLabel = "取消",
  } = options;

  return new Promise((resolve) => {
    const existing = document.getElementById("global-confirm-dialog");
    if (existing) existing.remove();

    const overlay = document.createElement("div");
    overlay.id = "global-confirm-dialog";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    Object.assign(overlay.style, {
      position: "fixed",
      inset: "0",
      zIndex: "9999",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(0,0,0,0.4)",
    });

    const panel = document.createElement("div");
    Object.assign(panel.style, {
      background: "#fff",
      borderRadius: "12px",
      width: "100%",
      maxWidth: "28rem",
      margin: "0 1rem",
      boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
      overflow: "hidden",
    });

    const header = document.createElement("div");
    Object.assign(header.style, {
      padding: "1rem 1.25rem",
      borderBottom: "1px solid #e5e7eb",
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
    });

    const iconWrap = document.createElement("div");
    Object.assign(iconWrap.style, {
      width: "2.25rem",
      height: "2.25rem",
      borderRadius: "9999px",
      background: "#dcfce7",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: "0",
      color: "#16a34a",
      fontSize: "1.125rem",
    });
    iconWrap.innerHTML = '<i class="ri-question-line"></i>';

    const titleEl = document.createElement("h3");
    Object.assign(titleEl.style, {
      margin: "0",
      fontSize: "0.875rem",
      fontWeight: "600",
      color: "#111827",
    });
    titleEl.textContent = title;
    header.append(iconWrap, titleEl);

    const body = document.createElement("div");
    Object.assign(body.style, {
      padding: "1rem 1.25rem",
      fontSize: "0.875rem",
      color: "#4b5563",
      lineHeight: "1.625",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    });
    body.textContent = message;

    const footer = document.createElement("div");
    Object.assign(footer.style, {
      padding: "1rem 1.25rem",
      borderTop: "1px solid #e5e7eb",
      display: "flex",
      justifyContent: "flex-end",
      gap: "0.5rem",
    });

    const cancelBtn = document.createElement("button");
    cancelBtn.type = "button";
    cancelBtn.textContent = cancelLabel;
    Object.assign(cancelBtn.style, {
      padding: "0.5rem 1rem",
      borderRadius: "0.5rem",
      border: "1px solid #d1d5db",
      background: "#fff",
      color: "#4b5563",
      fontSize: "0.875rem",
      fontWeight: "500",
      cursor: "pointer",
    });

    const okBtn = document.createElement("button");
    okBtn.type = "button";
    okBtn.textContent = confirmLabel;
    Object.assign(okBtn.style, {
      padding: "0.5rem 1rem",
      borderRadius: "0.5rem",
      border: "none",
      background: "#22c55e",
      color: "#fff",
      fontSize: "0.875rem",
      fontWeight: "500",
      cursor: "pointer",
    });

    const close = (result: boolean) => {
      overlay.remove();
      resolve(result);
    };

    cancelBtn.addEventListener("click", () => close(false));
    okBtn.addEventListener("click", () => close(true));
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) close(false);
    });

    footer.append(cancelBtn, okBtn);
    panel.append(header, body, footer);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);
    okBtn.focus();
  });
}

type ToastType = "error" | "success" | "warning";

interface ToastOptions {
  type?: ToastType;
  duration?: number;
  /** 贴合该元素显示；传入点击事件的 currentTarget 即可 */
  anchor?: Element | EventTarget | null;
}

function resolveAnchor(anchor?: Element | EventTarget | null): Element | null {
  if (!anchor) return null;
  if (anchor instanceof Element) return anchor;
  return null;
}

/**
 * 轻提示（toast / toptip）：有 anchor 时贴合按钮，否则居中靠上
 */
export function showToast(message: string, options: ToastOptions = {}) {
  const { type = "warning", duration = 2200, anchor } = options;
  const el = resolveAnchor(anchor);

  const existing = document.getElementById("global-toast-tip");
  if (existing) existing.remove();

  const palette =
    type === "success"
      ? { bg: "#22c55e", icon: "ri-checkbox-circle-line" }
      : type === "error"
        ? { bg: "#ef4444", icon: "ri-error-warning-line" }
        : { bg: "#f59e0b", icon: "ri-error-warning-line" };

  const tip = document.createElement("div");
  tip.id = "global-toast-tip";
  tip.setAttribute("role", "status");
  Object.assign(tip.style, {
    position: "fixed",
    zIndex: "10000",
    padding: "0.5rem 0.875rem",
    borderRadius: "0.5rem",
    background: palette.bg,
    color: "#fff",
    fontSize: "0.8125rem",
    fontWeight: "500",
    boxShadow: "0 10px 15px -3px rgba(0,0,0,0.15), 0 4px 6px -4px rgba(0,0,0,0.1)",
    display: "flex",
    alignItems: "center",
    gap: "0.375rem",
    maxWidth: "min(90vw, 16rem)",
    pointerEvents: "none",
    whiteSpace: "nowrap",
    opacity: "0",
    transition: "opacity 0.15s ease",
  });

  tip.innerHTML = `<i class="${palette.icon}"></i><span></span>`;
  const textEl = tip.querySelector("span");
  if (textEl) textEl.textContent = message;

  document.body.appendChild(tip);

  const tipRect = tip.getBoundingClientRect();
  const gap = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top: number;
  let left: number;

  if (el) {
    const rect = el.getBoundingClientRect();
    // 优先显示在按钮上方，空间不足则下方
    const above = rect.top - tipRect.height - gap;
    const below = rect.bottom + gap;
    top = above >= gap ? above : Math.min(below, vh - tipRect.height - gap);
    left = rect.left + rect.width / 2 - tipRect.width / 2;
  } else {
    top = 16;
    left = vw / 2 - tipRect.width / 2;
  }

  left = Math.max(8, Math.min(left, vw - tipRect.width - 8));
  top = Math.max(8, Math.min(top, vh - tipRect.height - 8));

  tip.style.top = `${top}px`;
  tip.style.left = `${left}px`;
  // 强制回流后再显示，避免闪一下
  requestAnimationFrame(() => {
    tip.style.opacity = "1";
  });

  window.setTimeout(() => {
    tip.style.opacity = "0";
    window.setTimeout(() => tip.remove(), 200);
  }, duration);
}

export function toastWarning(message: string, anchor?: Element | EventTarget | null) {
  showToast(message, { type: "warning", anchor });
}

export function toastError(message: string, anchor?: Element | EventTarget | null) {
  showToast(message, { type: "error", anchor });
}

export function toastSuccess(message: string, anchor?: Element | EventTarget | null) {
  showToast(message, { type: "success", anchor });
}
